/**
 * How a captured photo is encoded once every pixel pass (crop, filter, rotation, mirror)
 * is done — the bytes that go into the session, the strip, and the upload queue.
 *
 * Both capture paths have already JPEG-compressed the frame by the time it reaches the
 * final encode: DSLR frames come back from `imageUtils.resize` as JPEG at the preset's
 * quality, webcam frames from `capturePreviewAsBlob` the same way. PNG therefore does not
 * restore detail — it stores an already-lossy frame losslessly, at roughly 3× the bytes of
 * a JPEG at the same quality (~3.3 MB vs ~1 MB per 1080p photo in the field). On a slow
 * venue link that is the difference between the upload queue keeping up and guests
 * waiting, so the operator picks the format per camera, next to the resolution.
 */

import { useFeatureFlagsStore } from '@renderer/stores/featureFlagsStore'

export type CaptureFormat = 'png' | 'jpeg'

export const CAPTURE_FORMATS: readonly CaptureFormat[] = ['png', 'jpeg']

/**
 * What every booth produced before this setting existed, and the default while
 * CAPTURE_JPEG_DEFAULT_FLAG is off.
 */
export const DEFAULT_CAPTURE_FORMAT: CaptureFormat = 'png'

/**
 * Studio flag (config/featureFlags.ts, surface "app"): a camera whose operator never
 * picked a Photo Format saves JPEG instead of PNG. An explicit choice in Camera Settings
 * wins either way. Off — or unknown, on an older Studio or before the first flag fetch —
 * keeps PNG, so switching it off is a complete rollback. Flags are fetched at startup,
 * so a flip reaches a booth on its next launch.
 */
export const CAPTURE_JPEG_DEFAULT_FLAG = 'capture_jpeg_default'

/**
 * The operator's explicit choice, or undefined when they never made one. Persisted
 * settings are operator-editable JSON, so anything unrecognised counts as no choice.
 */
export function parseCaptureFormat(value: unknown): CaptureFormat | undefined {
  return CAPTURE_FORMATS.includes(value as CaptureFormat) ? (value as CaptureFormat) : undefined
}

/** The format for a camera with no explicit choice, given the flag's state. */
export function captureFormatDefault(jpegByDefault: boolean): CaptureFormat {
  return jpegByDefault ? 'jpeg' : DEFAULT_CAPTURE_FORMAT
}

/** Effective format: the operator's choice when there is one, otherwise `fallback`. */
export function normaliseCaptureFormat(
  value: unknown,
  fallback: CaptureFormat = DEFAULT_CAPTURE_FORMAT
): CaptureFormat {
  return parseCaptureFormat(value) ?? fallback
}

/**
 * Effective format outside React (capture path, telemetry): the operator's choice, else
 * the default the flag decides. Components read the flag with `useFeatureFlag` instead,
 * so they re-render when it changes.
 */
export function resolveCaptureFormat(value: unknown): CaptureFormat {
  const jpegByDefault = useFeatureFlagsStore.getState().getFlag(CAPTURE_JPEG_DEFAULT_FLAG, false)
  return normaliseCaptureFormat(value, captureFormatDefault(jpegByDefault))
}

export interface ImageEncoding {
  type: 'image/png' | 'image/jpeg'
  /** Encoder quality for JPEG; PNG ignores it. */
  quality?: number
  extension: 'png' | 'jpg'
}

/** PNG — the historical output, and the right choice for a pass another pass will decode again. */
export const LOSSLESS_ENCODING: ImageEncoding = { type: 'image/png', extension: 'png' }

/** Same floor as the lowest preset and the custom quality slider. */
const MIN_JPEG_QUALITY = 0.4

/**
 * Final encoding of a fresh capture. JPEG reuses the preset's quality — the knob that
 * already drives the earlier JPEG pass and the size estimate shown next to it.
 */
export function captureEncodingFor(
  format: CaptureFormat,
  quality: number | undefined
): ImageEncoding {
  if (format === 'png') return LOSSLESS_ENCODING
  const jpegQuality =
    typeof quality === 'number' && Number.isFinite(quality)
      ? Math.min(Math.max(quality, MIN_JPEG_QUALITY), 1)
      : 1
  return { type: 'image/jpeg', quality: jpegQuality, extension: 'jpg' }
}

/**
 * Quality for a pass that rewrites an already-captured JPEG (post-capture filters). Same
 * value as the watermark pass, the other re-encode an uploaded photo can go through.
 */
export const REENCODE_JPEG_QUALITY = 0.95

/**
 * Encoding for a pass that rewrites an existing photo: keep the format it was captured
 * in, so picking a filter never turns a JPEG booth's photos back into 3 MB PNGs. Anything
 * that is not JPEG keeps the historical PNG output.
 */
export function reencodingFor(sourceType: string | undefined): ImageEncoding {
  return sourceType === 'image/jpeg'
    ? { type: 'image/jpeg', quality: REENCODE_JPEG_QUALITY, extension: 'jpg' }
    : LOSSLESS_ENCODING
}
